import { Description, Input, Label, ListBox, Select, TextField } from "@heroui/react";
import type { ActivityLevel, NutritionProfile, Sex } from "../api/client";

const SEXES: { value: Sex; label: string }[] = [
  { value: "female", label: "Dona" },
  { value: "male", label: "Home" },
];

const ACTIVITIES: { value: ActivityLevel; label: string }[] = [
  { value: "sedentary", label: "Sedentària" },
  { value: "light", label: "Lleugera" },
  { value: "moderate", label: "Moderada" },
  { value: "active", label: "Molt activa" },
];

const NONE = "none";

function toNumber(v: string, int: boolean): number | null {
  const n = int ? parseInt(v, 10) : parseFloat(v.replace(",", "."));
  return Number.isFinite(n) ? n : null;
}

interface Props {
  value: NutritionProfile;
  onChange: (v: NutritionProfile) => void;
}

/** Camps del perfil nutricional: s'usen per calcular l'energia recomanada a la revisió de la setmana. */
export function NutritionProfileEditor({ value, onChange }: Props) {
  const set = (patch: Partial<NutritionProfile>) => onChange({ ...value, ...patch });

  return (
    <div className="flex flex-col gap-2">
      <div className="text-sm font-semibold">Perfil nutricional</div>
      <div className="flex flex-wrap items-end gap-3">
        <TextField
          className="w-32"
          type="number"
          value={value.birthYear == null ? "" : String(value.birthYear)}
          onChange={(v) => set({ birthYear: toNumber(v, true) })}
        >
          <Label>Any de naixement</Label>
          <Input inputMode="numeric" />
        </TextField>
        <ProfileSelect
          label="Sexe"
          options={SEXES}
          value={value.sex}
          onChange={(sex) => set({ sex })}
        />
        <TextField
          className="w-24"
          type="number"
          value={value.heightCm == null ? "" : String(value.heightCm)}
          onChange={(v) => set({ heightCm: toNumber(v, true) })}
        >
          <Label>Alçada (cm)</Label>
          <Input inputMode="numeric" />
        </TextField>
        <TextField
          className="w-24"
          type="number"
          value={value.weightKg == null ? "" : String(value.weightKg)}
          onChange={(v) => set({ weightKg: toNumber(v, false) })}
        >
          <Label>Pes (kg)</Label>
          <Input inputMode="decimal" />
        </TextField>
        <ProfileSelect
          label="Activitat"
          options={ACTIVITIES}
          value={value.activityLevel}
          onChange={(activityLevel) => set({ activityLevel })}
        />
      </div>
      <Description>
        Opcional. Serveix per estimar l&apos;energia recomanada a «Revisa la setmana». Als menors de 18
        anys cal el pes.
      </Description>
    </div>
  );
}

function ProfileSelect<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { value: T; label: string }[];
  value: T | null;
  onChange: (v: T | null) => void;
}) {
  return (
    <Select
      className="min-w-[140px]"
      selectedKey={value ?? NONE}
      onSelectionChange={(k) => onChange(k === NONE ? null : (k as T))}
    >
      <Label>{label}</Label>
      <Select.Trigger>
        <Select.Value />
        <Select.Indicator />
      </Select.Trigger>
      <Select.Popover>
        <ListBox>
          {[{ value: NONE, label: "Sense indicar" }, ...options].map((o) => (
            <ListBox.Item key={o.value} id={o.value} textValue={o.label}>
              {o.label}
              <ListBox.ItemIndicator />
            </ListBox.Item>
          ))}
        </ListBox>
      </Select.Popover>
    </Select>
  );
}
